import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useSessionStore } from "@/stores";
import CreateConsumptionRequest from "@/models/requests/CreateConsumptionRequest";
import { IConsumption } from "@/models/responses/IConsumption";
import DraftConsumption from "@/models/frontend/DraftConsumption";
import { CONSUMPTION_HEAD_STATUS } from "@/models/Enums";

export const useConsumptionsStore = defineStore("consumptions", {
  state: () => ({
    error: "" as any,
    data: [] as IConsumption[]
  }),
  actions: {
    async fetchAll(includeHistory: boolean = false) {
      const authStore = useAuthStore();
      const sessionStore = useSessionStore();

      const { isAuthenticated } = storeToRefs(authStore);
      const { selectedSiteId } = storeToRefs(sessionStore);

      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .get<IConsumption[]>(`v1.0/consumptions?siteId=${selectedSiteId.value}&status=${CONSUMPTION_HEAD_STATUS.pending}${includeHistory ? "&forcedIncludeTimeSpan=10.00:00:00" : ""}&forcedExportFailed=true`)
        .then((response) => (this.data = response.data))
        .catch((error) => (this.error = error));
    },
    async fetch(id: number) {
      this.error = "";
      await api
        .get<IConsumption>(`v1.0/consumptions/${id}`)
        .then((response) => {
          const index = this.data.findIndex((item) => item.id === id);
          if (index === -1) {
            this.data.push(response.data);
          } else {
            this.data[index] = response.data;
          }
         })
        .catch((error) => (this.error = error));
    },
    async create(consumption: CreateConsumptionRequest, onfulfilled: (data: IConsumption) => PromiseLike<void>, onrejected: (reason: any) => void | PromiseLike<void>) {
      this.error = "";
      await api
        .post("v1.0/consumptions", consumption)
        .then(async (response) => {
          const index = this.data.findIndex((x) => x.id === response.data.id);
          if (index === -1) this.data.push(response.data);
          else this.data[index] = response.data;
          await onfulfilled(response.data);
         })
         .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async delete(id: number) {
      this.error = "";
      await api
        .delete(`v1.0/consumptions/${id}`)
        .then(() => (this.data = this.data.filter((item) => item.id !== id)))
        .catch((error) => (this.error = error));
    },
    async accept(id: number) {
      this.error = "";
      await api
        .put(`v1.0/consumptions/${id}/accept`)
        .then((response) => {
          const index = this.data.findIndex((item) => item.id === id);
          if (index !== -1) {
            this.data[index] = response.data;
          }
        })
        .catch((error) => (this.error = error));
    },
    async retryExport(id: number, onfulfilled: (data: IConsumption) => PromiseLike<void>, onrejected: (reason: any) => void | PromiseLike<void>) {
      this.error = "";
      await api
        .post(`v1.0/consumptions/${id}/export`)
        .then(async (response) => {
          const index = this.data.findIndex((item) => item.id === id);
          if (index !== -1) {
            this.data[index] = response.data;
          }
          await onfulfilled(response.data);
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async cancelExport(id: number) {
      this.error = "";
      await api
        .put(`v1.0/consumptions/${id}/cancel-export`)
        .then((response) => {
          const index = this.data.findIndex((item) => item.id === id);
          if (index !== -1) {
            this.data[index] = response.data;
          }
        })
        .catch((error) => (this.error = error));
    },
    async saveDraft(referenceCode: string, data: DraftConsumption | null, onfulfilled: () => void, onrejected: (reason: any) => void | PromiseLike<void>) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      await api
        .post(`v1.0/consumptions/draft?siteId=${selectedSiteId.value}&referenceCode=${referenceCode}`, data)
        .then(() => {
          onfulfilled();
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async loadDraft(referenceCode: string): Promise<DraftConsumption | null> {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      return await api
        .get<DraftConsumption>(`v1.0/consumptions/draft?siteId=${selectedSiteId.value}&referenceCode=${referenceCode}`)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          this.error = error;
          return null;
        });
    },
  },
  getters: {
    getById: (state) => (id: number): IConsumption => {
      return <IConsumption>state.data.find((item) => item.id === id);
    },
    getByCode: (state) => (code: string): IConsumption => {
      return <IConsumption>state.data.find((item) => item.referenceCode.toLowerCase() === code.toLowerCase());
    },
  },
});
